<template>
  <v-card
    v-if="item"
    id="screenContent"
    class="white vetalfa-view"
    :max-width="_getMaxWidth()"
    flat
  >
    <div class="vetalfa-global-header" ref="screenHeader">
      <vetalfa-small-header
        showBreadCrumbAndTitle
        hasHeaderOptions
        :title="!!item ? item.name : ''"
        :breadcrumb="
          !!topicsSections && !!content
            ? topicsSections[content.contentIndex].text
            : ''
        "
      ></vetalfa-small-header>
      <v-row no-gutters justify="center">
        <vetalfa-drugs-header
          :isDrugsMode="true"
          :name="
            !!topicsSections && !!content
              ? topicsSections[content.contentIndex].text
              : ''
          "
          :references="item.references"
          :description="!!topic ? topic.name : ''"
          v-on:resize="_setupScreenSizes()"
          class="vetalfa-max-width mt-4 mb-2 px-2"
        >
          <template v-slot:leftSpace>
            <v-dialog
              transition="scroll-x-transition"
              content-class="item-content-navigation-menu"
              v-model="itemContentDialog"
              v-if="$route.name === 'item-content'"
              offset-y="-50"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mt-4"
                  elevation="0"
                  x-small
                  v-bind="attrs"
                  v-on="on"
                  fab
                  color="rgba(242, 242, 242, 0.8)"
                >
                  <v-icon size="22">{{ _icon("menuBurguer ") }}</v-icon>
                </v-btn>
              </template>
              <vetalfa-general-tab
                v-if="itemContentDialog"
                :sections="item.sections"
                :references="item.references"
                :itemName="item.name"
                :itemId="item.id"
                v-on:openContent="openItemContentFromDialogMenu"
                topics
                class="ma-0 pa-0 pr-2"
                :class="[_isIos() ? 'pt-12' : 'pt-6']"
              ></vetalfa-general-tab>
            </v-dialog>
          </template>
        </vetalfa-drugs-header>
      </v-row>
    </div>
    <div ref="screenContent">
      <vetalfa-sections
        v-if="content"
        :sections="topicsSections[content.contentIndex].sections"
      ></vetalfa-sections>
      <div
        class="white"
        :style="`height: ${_xsOnly() ? '400' : '500'}px`"
      ></div>
      <v-bottom-navigation
        ref="bottomNav"
        fixed
        app
        v-if="!!previousAndNextContents"
        :style="{
          zIndex: '10',
          transform: bottomNavTransform,
          height: 'auto',
          minHeight: '56px',
          transition: 'transform 0.5s ease',
        }"
        class="centered-bottom-nav white"
      >
        <v-row
          align="center"
          :justify="
            !!previousAndNextContents.previous ? 'space-between' : 'end'
          "
          class="ma-0 pb-2 pt-4 px-0 white vetalfa-max-width"
        >
          <v-col
            v-if="!!previousAndNextContents.previous"
            :cols="!previousAndNextContents.next ? 8 : 6"
            class="ma-0 pa-0"
          >
            <p
              :style="{
                fontSize: screenTextSizes,
                lineHeight: screenTextSizes,
              }"
              class="text-left vetalfa-pointer pa-0 ma-0"
              @click="openItemContentFromBottomButton(content.contentIndex - 1)"
            >
              <v-icon
                :size="screenIconSizes"
                color="primary"
                class="ma-0 pa-0 mr-2 pl-1"
              >
                {{ _icon("leftArrow") }}
              </v-icon>
              <span class="font-weight-bold primary--text">{{
                ellipsizeStringToWidth(
                  topicsSections[content.contentIndex - 1].text
                )
              }}</span>
            </p>
          </v-col>
          <v-col
            v-if="!!previousAndNextContents.next"
            :cols="!previousAndNextContents.previous ? 8 : 6"
            class="ma-0 pa-0"
          >
            <p
              @click="openItemContentFromBottomButton(content.contentIndex + 1)"
              :style="{
                fontSize: screenTextSizes,
                lineHeight: screenTextSizes,
              }"
              class="text-right vetalfa-pointer pa-0 ma-0"
            >
              <span class="font-weight-bold primary--text">{{
                ellipsizeStringToWidth(
                  topicsSections[content.contentIndex + 1].text
                )
              }}</span>
              <v-icon
                :size="screenIconSizes"
                color="primary"
                class="ma-0 pa-0 ml-2 pr-1"
              >
                {{ _icon("rightArrow") }}
              </v-icon>
            </p>
          </v-col>
        </v-row>
      </v-bottom-navigation>
      <vetalfa-references-dialog></vetalfa-references-dialog>
    </div>
    <vetalfa-ai-content-assistant
      v-if="item"
      :contextData="{ item, topicsSections }"
      pageType="itemContent"
      :pageName="item.name"
      :bottomOffset="64"
    />
  </v-card>
</template>

<style>
.centered-bottom-nav {
  margin-left: auto;
  margin-right: auto;
  left: 0;
  right: 0;
  padding-bottom: calc(0px + env(safe-area-inset-bottom));
}
.item-content-navigation-menu {
  width: 75%;
  left: 0px;
  position: absolute;
  top: 0;
  padding-right: 0px;
  padding-left: 0px;
  margin: 0px;
  background: white;
  bottom: 0px;
  max-width: 400px;
}
</style>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import ReferencesDialog from "@/components/ReferencesDialog.vue";
import {
  mdiChevronLeft,
  mdiChevronRight,
  mdiChevronUp,
  mdiFormatListBulleted,
} from "@mdi/js";
export default {
  name: "ItemContent",
  components: {
    "vetalfa-references-dialog": ReferencesDialog,
  },
  computed: {
    ...mapGetters("topics", ["item", "itemContent", "topic"]),
    screenTextSizes() {
      return this._isMobile() ? "14px" : "16px";
    },
    screenIconSizes() {
      return this._isMobile() ? 20 : 24;
    },
    content: {
      get() {
        return this.itemContent;
      },
      set(val) {
        this.setItemContent(val);
      },
    },
    topicsSections() {
      let macroTopics = [];
      let macroTopicIndex = -1;
      for (let section of this.item.sections) {
        if (section.style == 0) {
          macroTopicIndex++;
          macroTopics.push({ ...section, sections: [] });
        } else {
          macroTopics[macroTopicIndex].sections.push(section);
        }
      }
      return macroTopics;
    },
    previousAndNextContents() {
      let obj = { previous: null, next: null };
      if (!!this.content) {
        obj.previous = this.topicsSections[this.content.contentIndex - 1];
        obj.next = this.topicsSections[this.content.contentIndex + 1];
      }
      return obj;
    },

    sectionIndexRoute() {
      return this.$route.params.sectionIndex;
    },
    contentIndexRoute() {
      return this.$route.params.contentIndex;
    },
  },
  methods: {
    ...mapActions("topics", ["openTopicItem"]),
    ...mapActions("preferences", ["trackContentOpen"]),
    ...mapActions("general", [
      "routePathRedirect",
      "popLastCrumbItem",
      "logEvent",
    ]),
    ...mapMutations("topics", ["setItemContent"]),

    openItemContentFromBottomButton(content) {
      this.logEvent("openItemContentFromBottomButton");
      this.openContent(content);
    },
    async prepareContent() {
      if (!this.item || this.item.id !== this.$route.params.itemId) {
        await this.openTopicItem({ itemId: this.$route.params.itemId });
      }
      this.openContent(
        parseInt(this.$route.params.contentIndex),
        parseInt(this.$route.params.sectionIndex),
        true,
        true
      );
      this.trackContentOpen();
    },
    openItemContentFromDialogMenu({ contentIndex, sectionIndex }) {
      this.logEvent("openItemContentFromDialogMenu");
      if (
        contentIndex != this.contentIndexRoute ||
        sectionIndex != this.sectionIndexRoute
      ) {
        this.openContent(contentIndex, sectionIndex, false, true);
      }
      this.itemContentDialog = false;
    },
    handleScroll(contentIndex) {
      if (this.$route.name !== "item-content") {
        window.removeEventListener("scroll", this.scrollFunction);
        return;
      }
      if (this.content) {
        const title = this.topicsSections[contentIndex].sections.filter(
          (s) => s.style == 1
        )[0].text;
        if (
          !!document.getElementById(this._createIdentifier(title)) &&
          !!this.$refs.screenHeader
        ) {
          const targetOffsetHeight = this.$refs.screenHeader.offsetHeight;
          const offset =
            document.getElementById(this._createIdentifier(title)).offsetTop -
            targetOffsetHeight -
            36;
          this.content["scrollTop"] = offset;
          let minDistance = Infinity;
          let nearestElement = null;
          this.topicsSections[contentIndex].sections
            .filter((s) => s.style == 1)
            .forEach((item, index) => {
              const element = document.getElementById(
                this._createIdentifier(item.text)
              );
              if (element) {
                const rect = element.getBoundingClientRect();
                const distance = Math.abs(rect.top - targetOffsetHeight);
                if (distance < minDistance) {
                  minDistance = distance;
                  nearestElement = index;
                }
              }
            });
          if (nearestElement != null) {
            this.$route.params.sectionIndex = nearestElement;
          }
        }
      }
    },
    binarySearchToFit(str, testElement, targetWidth, ellipsis) {
      let start = 0;
      let end = str.length;

      while (start < end) {
        const mid = Math.floor((start + end + 1) / 2);
        testElement.textContent = str.slice(0, mid);

        if (
          testElement.offsetWidth +
            ellipsis.length * parseInt(testElement.style.fontSize) <=
          targetWidth
        ) {
          start = mid;
        } else {
          end = mid - 1;
        }
      }

      return start;
    },
    ellipsizeStringToWidth(str) {
      if (!this._isMobile()) {
        return str;
      }
      const ellipsis = "...";
      const targetWidth =
        this._getWindowWidth(this.screenIconSizes * 2.5) /
        (!!this.previousAndNextContents.previous &&
        !!this.previousAndNextContents.next
          ? 2
          : 1);
      const testElement = document.createElement("span");
      testElement.style.fontSize = "14px";
      testElement.style.position = "absolute";
      testElement.style.whiteSpace = "nowrap";
      testElement.style.visibility = "hidden";
      document.body.appendChild(testElement);
      const longestPrefixIndex = this.binarySearchToFit(
        str,
        testElement,
        targetWidth,
        ellipsis
      );

      const wasSliced = longestPrefixIndex < str.length;
      testElement.textContent =
        str.slice(0, longestPrefixIndex) + (wasSliced ? ellipsis : "");
      const result = testElement.textContent;
      document.body.removeChild(testElement);
      return result;
    },
    openContent(
      contentIndex,
      sectionIndex = 0,
      firstOpen = false,
      scroll = false
    ) {
      if (!firstOpen) {
        this.popLastCrumbItem();
      }
      this.$router
        .replace(
          `/item/${this.item.id}/content/${contentIndex}/section/${sectionIndex}`
        )
        .catch(() => {});
      this.content = {
        ...this.content,
        contentIndex,
        sectionIndex,
      };
      this.$nextTick(() => {
        this._setupScreenSizesSynchronous(this, 0);
        if (!!scroll) {
          this.$nextTick(() => {
            setTimeout(
              function () {
                this.scrollFunction = () => this.handleScroll(contentIndex);
                window.addEventListener("scroll", this.scrollFunction);
                if (!!this.content.scrollTop) {
                  this._smoothScrollBy(0, this.content.scrollTop);
                  this.content.scrollTop = 0;
                } else if (sectionIndex != 0) {
                  this.content["scrollTop"] = 0;
                  const title = this.topicsSections[
                    contentIndex
                  ].sections.filter((s) => s.style == 1)[sectionIndex].text;
                  const offset =
                    document.getElementById(this._createIdentifier(title))
                      .offsetTop -
                    this.$refs.screenHeader.offsetHeight -
                    36;
                  this._smoothScrollBy(0, offset);
                }
              }.bind(this),
              100
            );
          });
        }
      });
    },
  },
  data() {
    return {
      bottomNavTransform: "translateY(0)",
      mdiChevronUp,
      mdiChevronRight,
      mdiChevronLeft,
      mdiFormatListBulleted,
      lastScrollPosition: 0,
      scrollFunction: null,
      itemContentDialog: false,
    };
  },
  async mounted() {
    await this.prepareContent();
  },
  beforeRouteLeave(_to, _from, next) {
    window.removeEventListener("scroll", this.scrollFunction);
    next();
  },
  watch: {
    "$route.params.itemId": {
      handler: function () {
        this.prepareContent();
      },
      deep: true,
      immediate: false,
    },
    itemContentDialog(val) {
      if (val) this.logEvent("itemContentDialogButtonClick");
    },
  },
};
</script>